import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  CheckCircle2, Copy, Eye, EyeOff, KeyRound, LockKeyhole,
  RefreshCw, ShieldCheck, ShieldOff
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const initialPasswordForm = { current_password: "", new_password: "", confirm_password: "" };

function PasswordInput({ label, value, onChange, autoComplete = "current-password", placeholder = "", show, onToggle }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pr-11 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
        />
        <button type="button" onClick={onToggle} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-slate-700" aria-label={show ? "Sembunyikan password" : "Tampilkan password"}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function Security() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [passwordForm, setPasswordForm] = useState(initialPasswordForm);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const [setupPassword, setSetupPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [setupCode, setSetupCode] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [sessionInvalidated, setSessionInvalidated] = useState(false);

  const [disableForm, setDisableForm] = useState({ current_password: "", code: "" });
  const [disableLoading, setDisableLoading] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const response = await api.get("/auth/security");
      setStatus(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status keamanan akun tidak dapat dimuat");
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const changePassword = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      return toast.error("Konfirmasi password baru tidak sama");
    }
    setPasswordLoading(true);
    try {
      const response = await api.post("/auth/change-password", passwordForm);
      toast.success(response.data?.message || "Password berhasil diubah");
      setPasswordForm(initialPasswordForm);
      window.setTimeout(() => logout(), 800);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Password gagal diubah");
    } finally {
      setPasswordLoading(false);
    }
  };

  const beginSetup = async (event) => {
    event.preventDefault();
    setSetupLoading(true);
    setRecoveryCodes([]);
    try {
      const response = await api.post("/auth/2fa/setup", { current_password: setupPassword });
      setSetup(response.data);
      setSetupCode("");
      toast.success("QR Google Authenticator berhasil dibuat");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Setup 2FA gagal dibuat");
    } finally {
      setSetupLoading(false);
    }
  };

  const enableTwoFactor = async (event) => {
    event.preventDefault();
    setSetupLoading(true);
    try {
      const response = await api.post("/auth/2fa/enable", { code: setupCode.trim() });
      setRecoveryCodes(response.data?.recovery_codes || []);
      setSessionInvalidated(Boolean(response.data?.requires_relogin));
      setStatus((current) => ({ ...(current || {}), two_factor_enabled: true, recovery_codes_remaining: (response.data?.recovery_codes || []).length }));
      setSetup(null);
      setSetupPassword("");
      setSetupCode("");
      toast.success(response.data?.message || "2FA berhasil diaktifkan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Kode Google Authenticator tidak valid");
    } finally {
      setSetupLoading(false);
    }
  };

  const disableTwoFactor = async (event) => {
    event.preventDefault();
    setDisableLoading(true);
    try {
      const response = await api.post("/auth/2fa/disable", {
        current_password: disableForm.current_password,
        code: disableForm.code.trim(),
      });
      toast.success(response.data?.message || "2FA berhasil dinonaktifkan");
      window.setTimeout(() => logout(), 800);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "2FA gagal dinonaktifkan");
    } finally {
      setDisableLoading(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success("Recovery code disalin");
    } catch {
      toast.error("Browser tidak mengizinkan copy otomatis");
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-5 sm:p-8 lg:p-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Keamanan Akun</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-[-0.04em] text-slate-950">Password & Google Authenticator</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">Pengaturan ini berlaku untuk akun login <span className="font-semibold text-slate-700">{user?.name}</span>. 2FA dapat diaktifkan atau dinonaktifkan secara mandiri.</p>
        </div>
        <button onClick={loadStatus} disabled={loadingStatus || sessionInvalidated} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loadingStatus ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {sessionInvalidated && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
          Pengaturan keamanan berubah dan sesi lama sudah dinonaktifkan. Simpan recovery code terlebih dahulu, lalu login kembali.
        </div>
      )}

      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white"><LockKeyhole className="h-5 w-5" /></div>
            <div>
              <h2 className="text-xl font-black text-slate-950">Ganti Password</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">Tersedia untuk Platform Owner dan seluruh akun mitra. Setelah berhasil, semua sesi lama akan tidak berlaku.</p>
            </div>
          </div>

          <form onSubmit={changePassword} className="mt-6 space-y-4">
            <PasswordInput label="Password saat ini" value={passwordForm.current_password} onChange={(value) => setPasswordForm((current) => ({ ...current, current_password: value }))} show={showPasswords} onToggle={() => setShowPasswords((value) => !value)} />
            <PasswordInput label="Password baru" value={passwordForm.new_password} onChange={(value) => setPasswordForm((current) => ({ ...current, new_password: value }))} autoComplete="new-password" placeholder="Min. 8 karakter, huruf besar, kecil & angka" show={showPasswords} onToggle={() => setShowPasswords((value) => !value)} />
            <PasswordInput label="Konfirmasi password baru" value={passwordForm.confirm_password} onChange={(value) => setPasswordForm((current) => ({ ...current, confirm_password: value }))} autoComplete="new-password" show={showPasswords} onToggle={() => setShowPasswords((value) => !value)} />
            <button type="submit" disabled={passwordLoading || sessionInvalidated} className="w-full rounded-2xl bg-slate-950 py-3.5 text-sm font-bold text-white transition hover:bg-blue-600 disabled:opacity-50">
              {passwordLoading ? "Mengubah password…" : "Ganti Password"}
            </button>
          </form>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${status?.two_factor_enabled ? "bg-emerald-100 text-emerald-700" : "bg-blue-50 text-blue-600"}`}>
                {status?.two_factor_enabled ? <ShieldCheck className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-950">Google Authenticator 2FA</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">Tambahkan kode TOTP setelah password ketika login. Fitur ini opsional untuk Owner maupun akun mitra.</p>
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider ${status?.two_factor_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {loadingStatus ? "..." : status?.two_factor_enabled ? "Aktif" : "Nonaktif"}
            </span>
          </div>

          {!loadingStatus && !status?.two_factor_enabled && !setup && !recoveryCodes.length && (
            <form onSubmit={beginSetup} className="mt-6 space-y-4">
              <PasswordInput label="Konfirmasi password saat ini" value={setupPassword} onChange={setSetupPassword} show={showPasswords} onToggle={() => setShowPasswords((value) => !value)} />
              <button type="submit" disabled={setupLoading || sessionInvalidated} className="w-full rounded-2xl bg-blue-600 py-3.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50">
                {setupLoading ? "Membuat setup…" : "Aktifkan 2FA"}
              </button>
            </form>
          )}

          {setup && (
            <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50/60 p-5">
              <div className="text-sm font-black text-slate-900">1. Scan QR dengan Google Authenticator</div>
              <div className="mt-4 flex justify-center rounded-2xl bg-white p-5">
                <QRCodeSVG value={setup.otpauth_uri} size={190} level="M" includeMargin={false} />
              </div>
              <div className="mt-4 text-xs text-slate-500">Jika tidak bisa scan, masukkan setup key secara manual:</div>
              <div className="mt-2 break-all rounded-xl bg-slate-950 p-3 text-center font-mono text-sm tracking-wider text-white">{setup.secret}</div>

              <form onSubmit={enableTwoFactor} className="mt-5 space-y-3">
                <label className="block text-sm font-bold text-slate-800">2. Masukkan kode 6 digit untuk konfirmasi</label>
                <input required autoComplete="one-time-code" inputMode="numeric" value={setupCode} onChange={(event) => setSetupCode(event.target.value)} placeholder="123456" className="w-full rounded-2xl border border-blue-200 bg-white px-4 py-3 text-center font-mono text-lg tracking-[0.2em] outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                <div className="grid gap-2 sm:grid-cols-2">
                  <button type="submit" disabled={setupLoading} className="rounded-2xl bg-slate-950 py-3 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-50">{setupLoading ? "Memverifikasi…" : "Konfirmasi & Aktifkan"}</button>
                  <button type="button" onClick={() => { setSetup(null); setSetupCode(""); }} disabled={setupLoading} className="rounded-2xl border border-slate-200 bg-white py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Batal</button>
                </div>
              </form>
            </div>
          )}

          {recoveryCodes.length > 0 && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <div className="flex items-center gap-2 font-black text-amber-950"><CheckCircle2 className="h-5 w-5" /> 2FA berhasil diaktifkan</div>
              <p className="mt-2 text-sm leading-6 text-amber-900">Simpan recovery code berikut di tempat aman. Setiap kode hanya dapat digunakan satu kali dan tidak ditampilkan lagi setelah halaman ini ditutup.</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {recoveryCodes.map((code) => <div key={code} className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-center font-mono text-sm font-bold text-slate-800">{code}</div>)}
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <button type="button" onClick={copyRecoveryCodes} className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300 bg-white py-2.5 text-sm font-bold text-amber-900"><Copy className="h-4 w-4" /> Copy Recovery Code</button>
                <button type="button" onClick={logout} className="rounded-xl bg-slate-950 py-2.5 text-sm font-bold text-white">Saya Sudah Simpan · Login Ulang</button>
              </div>
            </div>
          )}

          {!loadingStatus && status?.two_factor_enabled && !recoveryCodes.length && (
            <form onSubmit={disableTwoFactor} className="mt-6 space-y-4">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
                2FA aktif. Recovery code tersisa: <span className="font-black">{status.recovery_codes_remaining ?? 0}</span>.
              </div>
              <PasswordInput label="Password saat ini" value={disableForm.current_password} onChange={(value) => setDisableForm((current) => ({ ...current, current_password: value }))} show={showPasswords} onToggle={() => setShowPasswords((value) => !value)} />
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Kode Google Authenticator / Recovery Code</label>
                <input required autoComplete="one-time-code" value={disableForm.code} onChange={(event) => setDisableForm((current) => ({ ...current, code: event.target.value }))} placeholder="123456" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center font-mono tracking-[0.16em] outline-none focus:border-rose-500 focus:bg-white focus:ring-4 focus:ring-rose-100" />
              </div>
              <button type="submit" disabled={disableLoading || sessionInvalidated} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 py-3.5 text-sm font-bold text-rose-700 transition hover:bg-rose-600 hover:text-white disabled:opacity-50">
                <ShieldOff className="h-4 w-4" /> {disableLoading ? "Menonaktifkan…" : "Nonaktifkan 2FA"}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
